<template>
  <div class="entity-list-page my-learning-page">
    <header class="list-header">
      <div>
        <h1>My courses</h1>
        <p class="list-sub">Track your progress and continue learning</p>
      </div>
      <div class="header-actions">
        <router-link :to="{ path: '/learning', query: { tab: 'browse' } }" class="btn btn-secondary">
          <i class="fas fa-search"></i>
          Browse courses
        </router-link>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading your learning dashboard...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="dashboard-content">
      <!-- Stats Overview -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon enrolled">
            <i class="fas fa-book-open"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.total_enrolled }}</h3>
            <p>Courses Enrolled</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon completed">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.completed }}</h3>
            <p>Courses Completed</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon in-progress">
            <i class="fas fa-clock"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.in_progress }}</h3>
            <p>In Progress</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon hours">
            <i class="fas fa-hourglass-half"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.total_hours }}h</h3>
            <p>Learning Time</p>
          </div>
        </div>
      </div>

      <!-- Course Filters -->
      <div class="filter-section">
        <div class="filter-tabs">
          <button
            @click="currentFilter = 'all'"
            :class="{ active: currentFilter === 'all' }"
            class="filter-tab"
          >
            All Courses ({{ enrolledCourses.length }})
          </button>
          <button
            @click="currentFilter = 'in_progress'"
            :class="{ active: currentFilter === 'in_progress' }"
            class="filter-tab"
          >
            In Progress ({{ inProgressCourses.length }})
          </button>
          <button
            @click="currentFilter = 'completed'"
            :class="{ active: currentFilter === 'completed' }"
            class="filter-tab"
          >
            Completed ({{ completedCourses.length }})
          </button>
        </div>
      </div>

      <!-- Courses List -->
      <div v-if="filteredCourses.length > 0" class="courses-grid">
        <div
          v-for="enrollment in filteredCourses"
          :key="enrollment.id"
          class="course-card"
        >
          <div class="course-thumbnail">
            <img
              v-if="enrollment.course.thumbnail"
              :src="getThumbnailUrl(enrollment.course.thumbnail)"
              :alt="enrollment.course.title"
            />
            <div v-else class="thumbnail-placeholder">
              <i class="fas fa-book"></i>
            </div>
            
            <!-- Progress Overlay -->
            <div class="progress-overlay">
              <div class="progress-bar-container">
                <div
                  class="progress-bar"
                  :style="{ width: `${enrollment.progress_percentage}%` }"
                ></div>
              </div>
              <span class="progress-text">{{ Math.round(enrollment.progress_percentage) }}% Complete</span>
            </div>
          </div>

          <div class="course-content">
            <div class="course-meta">
              <span class="course-type">{{ formatCourseType(enrollment.course.course_type) }}</span>
              <span v-if="enrollment.completed_at" class="completed-badge">
                <i class="fas fa-check-circle"></i>
                Completed
              </span>
            </div>

            <h3 class="course-title">{{ enrollment.course.title }}</h3>
            <p class="course-description">{{ enrollment.course.short_description }}</p>

            <div class="course-stats">
              <div class="stat">
                <i class="fas fa-clock"></i>
                <span>{{ formatDuration(enrollment.course.estimated_duration_hours) }}</span>
              </div>
              <div class="stat" v-if="enrollment.last_accessed_at">
                <i class="fas fa-calendar"></i>
                <span>Last accessed {{ formatRelativeTime(enrollment.last_accessed_at) }}</span>
              </div>
            </div>

            <!-- Video Progress Section -->
            <div v-if="enrollment.video_progress && enrollment.video_progress.length > 0" class="video-progress-section">
              <button 
                class="progress-toggle"
                @click.stop="toggleVideoProgress(enrollment.id)"
              >
                <div class="toggle-header">
                  <i class="fas fa-play-circle"></i>
                  <span class="toggle-title">Video Progress</span>
                  <span class="toggle-count">{{ enrollment.videos_completed || 0 }}/{{ enrollment.videos_total || enrollment.video_progress.length }}</span>
                </div>
                <i :class="expandedEnrollments[enrollment.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </button>
              
              <!-- Expandable Video List -->
              <div v-if="expandedEnrollments[enrollment.id]" class="video-progress-list">
                <div 
                  v-for="video in enrollment.video_progress" 
                  :key="video.video_id"
                  class="video-progress-item"
                  :class="{ completed: video.is_completed, 'in-progress': video.completion_percentage > 0 && !video.is_completed }"
                >
                  <div class="video-status-icon">
                    <i v-if="video.is_completed" class="fas fa-check-circle"></i>
                    <i v-else-if="video.completion_percentage > 0" class="fas fa-play-circle"></i>
                    <i v-else class="far fa-circle"></i>
                  </div>
                  <div class="video-progress-info">
                    <span class="video-title">{{ video.video_title }}</span>
                    <div class="video-progress-bar" v-if="video.completion_percentage > 0 && !video.is_completed">
                      <div class="progress-fill" :style="{ width: `${video.completion_percentage}%` }"></div>
                    </div>
                    <span class="video-status-text">
                      <template v-if="video.is_completed">Completed</template>
                      <template v-else-if="video.completion_percentage > 0">{{ Math.round(video.completion_percentage) }}% watched</template>
                      <template v-else>Not started</template>
                    </span>
                  </div>
                  <span class="video-duration">{{ formatVideoDuration(video.duration_seconds) }}</span>
                </div>
              </div>
            </div>

            <!-- Certification Requirements (if course has quiz) -->
            <div v-if="enrollment.course.quiz_questions && enrollment.course.quiz_questions.length > 0" class="certification-requirements">
              <div class="cert-header">
                <i class="fas fa-award"></i>
                <span>Certification Requirements</span>
              </div>
              <div class="requirement" :class="{ complete: enrollment.progress_percentage >= 95 }">
                <i :class="enrollment.progress_percentage >= 95 ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                <span>Watch all videos ({{ Math.round(enrollment.progress_percentage) }}%)</span>
              </div>
              <div class="requirement" :class="{ complete: enrollment.completion_criteria?.quiz_passed }">
                <i :class="enrollment.completion_criteria?.quiz_passed ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                <span>
                  Pass quiz ({{ enrollment.course.quiz_passing_score || 70 }}%)
                  <span v-if="enrollment.completion_criteria?.quiz_score" class="score-badge">
                    Best: {{ enrollment.completion_criteria.quiz_score }}%
                  </span>
                </span>
              </div>
            </div>

            <!-- Saved Research for this Course -->
            <div 
              v-if="getCourseResources(enrollment.course.id).length > 0" 
              class="saved-research-section"
            >
              <div 
                class="research-header-toggle"
                @click="toggleResearchExpanded(enrollment.course.id)"
              >
                <div class="research-header-left">
                  <i class="fas fa-book-medical"></i>
                  <span>My Research ({{ getCourseResources(enrollment.course.id).length }})</span>
                </div>
                <i class="fas" :class="isResearchExpanded(enrollment.course.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </div>
              
              <div v-show="isResearchExpanded(enrollment.course.id)" class="research-list">
                <div 
                  v-for="resource in getCourseResources(enrollment.course.id).slice(0, 3)"
                  :key="resource.id"
                  class="research-item"
                >
                  <a :href="resource.url" target="_blank" rel="noopener" class="research-link">
                    {{ truncateText(resource.title, 50) }}
                  </a>
                  <span v-if="resource.journal" class="research-journal">{{ resource.journal }}</span>
                </div>
                <router-link 
                  v-if="getCourseResources(enrollment.course.id).length > 3"
                  :to="`/learning/courses/${enrollment.course.id}?tab=research`"
                  class="view-all-research"
                >
                  View all {{ getCourseResources(enrollment.course.id).length }} articles
                </router-link>
              </div>
            </div>

            <div class="course-actions">
              <!-- Certificate download for completed courses -->
              <button
                v-if="enrollment.status === 'COMPLETED'"
                @click="downloadCertificate(enrollment.course.id)"
                class="btn btn-certificate btn-block"
              >
                <i class="fas fa-certificate"></i>
                Download Certificate
              </button>
              
              <!-- Take Quiz button (if quiz required, videos 95%+ complete, and quiz not passed) -->
              <router-link
                v-else-if="enrollment.course.quiz_questions && enrollment.course.quiz_questions.length > 0 && !enrollment.completion_criteria?.quiz_passed && enrollment.progress_percentage >= 95"
                :to="`/learning/courses/${enrollment.course.id}/quiz`"
                class="btn btn-primary btn-block"
              >
                <i class="fas fa-clipboard-check"></i>
                {{ enrollment.completion_criteria?.quiz_attempt_count > 0 ? 'Retake Quiz' : 'Take Quiz' }}
              </router-link>
              
              <!-- Quiz locked notice (if quiz required but videos not 95% complete) -->
              <div 
                v-else-if="enrollment.course.quiz_questions && enrollment.course.quiz_questions.length > 0 && !enrollment.completion_criteria?.quiz_passed && enrollment.progress_percentage < 95"
                class="quiz-locked-notice"
              >
                <i class="fas fa-lock"></i>
                <span>Quiz unlocks at 95% video completion ({{ Math.round(enrollment.progress_percentage) }}%)</span>
              </div>
              
              <!-- Continue Learning button -->
              <button
                v-else-if="!enrollment.completed_at"
                @click="continueCourse(enrollment)"
                class="btn btn-primary btn-block"
              >
                <i class="fas fa-play"></i>
                {{ enrollment.progress_percentage > 0 ? 'Continue Learning' : 'Start Course' }}
              </button>
              
              <router-link
                :to="`/learning/courses/${enrollment.course.id}`"
                class="btn btn-secondary btn-block"
              >
                <i class="fas fa-info-circle"></i>
                Course Details
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <i class="fas fa-graduation-cap"></i>
        <h2>{{ getEmptyStateTitle() }}</h2>
        <p>{{ getEmptyStateMessage() }}</p>
        <router-link to="/learning" class="btn btn-primary">
          <i class="fas fa-search"></i>
          Browse Course Catalog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { lmsAPI } from '@/services/lmsAPI.js'
import api from '@/services/api'
import { useToast } from 'vue-toastification'
import whitelabelBranding from '@/services/whitelabelBrandingService'

export default {
  name: 'MyLearning',
  setup() {
    const router = useRouter()
    const toast = useToast()
    
    // Get organization colors for theming
    const primaryColor = computed(() => whitelabelBranding.getColor('primary') || '#667eea')
    const secondaryColor = computed(() => whitelabelBranding.getColor('secondary') || '#764ba2')
    const accentColor = computed(() => whitelabelBranding.getColor('accent') || '#667eea')

    const enrolledCourses = ref([])
    const loading = ref(true)
    const currentFilter = ref('all')
    const expandedEnrollments = ref({})
    
    // Research Library state
    const resourcesByCourse = ref({})
    const expandedResearch = ref({})

    const stats = computed(() => {
      const total = enrolledCourses.value.length
      const completed = enrolledCourses.value.filter(e => e.completed_at).length
      const inProgress = enrolledCourses.value.filter(e => !e.completed_at && e.progress_percentage > 0).length
      const totalHours = enrolledCourses.value.reduce((sum, e) => {
        return sum + (e.course.estimated_duration_hours || 0)
      }, 0)

      return {
        total_enrolled: total,
        completed: completed,
        in_progress: inProgress,
        total_hours: Math.round(totalHours)
      }
    })

    const inProgressCourses = computed(() => {
      return enrolledCourses.value.filter(e => !e.completed_at && e.progress_percentage > 0)
    })

    const completedCourses = computed(() => {
      return enrolledCourses.value.filter(e => e.completed_at)
    })

    const filteredCourses = computed(() => {
      if (currentFilter.value === 'in_progress') {
        return inProgressCourses.value
      } else if (currentFilter.value === 'completed') {
        return completedCourses.value
      }
      return enrolledCourses.value
    })

    const loadEnrolledCourses = async () => {
      loading.value = true
      try {
        const response = await lmsAPI.getEnrollments({ enrolled_only: true })
        enrolledCourses.value = response.data.results || response.data
        
        // Load saved research for all courses
        loadResearchByCourse()
      } catch (err) {
        console.error('Error loading enrolled courses:', err)
        toast.error('Failed to load your courses')
      } finally {
        loading.value = false
      }
    }
    
    const loadResearchByCourse = async () => {
      try {
        const response = await lmsAPI.getResourcesByCourse()
        const coursesWithResources = response.data.courses_with_resources || []
        
        // Build a lookup map by course ID
        const lookup = {}
        for (const courseGroup of coursesWithResources) {
          lookup[courseGroup.course.id] = courseGroup.resources || []
        }
        resourcesByCourse.value = lookup
      } catch (err) {
        console.error('Error loading research by course:', err)
        // Non-critical - don't show error
      }
    }
    
    const getCourseResources = (courseId) => {
      return resourcesByCourse.value[courseId] || []
    }
    
    const isResearchExpanded = (courseId) => {
      return expandedResearch.value[courseId] || false
    }
    
    const toggleResearchExpanded = (courseId) => {
      expandedResearch.value[courseId] = !expandedResearch.value[courseId]
    }
    
    const truncateText = (text, maxLength) => {
      if (!text || text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }

    const continueCourse = async (enrollment) => {
      try {
        // Get course videos to find the last watched or first video
        const videosResponse = await lmsAPI.getVideos({ course: enrollment.course.id })
        const videos = videosResponse.data.results || videosResponse.data

        if (videos.length === 0) {
          toast.info('Course content is not yet available')
          return
        }

        // Find first incomplete video or the first video
        const nextVideo = videos.find(v => !v.is_completed) || videos[0]

        // Navigate to video player
        router.push({
          name: 'CourseVideoPlayer',
          params: {
            courseId: enrollment.course.id,
            videoId: nextVideo.id
          }
        })
      } catch (err) {
        console.error('Error loading course videos:', err)
        toast.error('Failed to load course content')
      }
    }

    const formatCourseType = (type) => {
      if (!type) return ''
      return type.replace('_', ' ')
    }

    const formatDuration = (hours) => {
      if (!hours) return 'Duration not specified'
      if (hours < 1) return `${Math.round(hours * 60)} minutes`
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    }

    const formatVideoDuration = (seconds) => {
      if (!seconds) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const toggleVideoProgress = (enrollmentId) => {
      expandedEnrollments.value[enrollmentId] = !expandedEnrollments.value[enrollmentId]
    }

    const downloadCertificate = async (courseId) => {
      try {
        const response = await api.get(`/lms/courses/${courseId}/certificate/`, {
          responseType: 'blob'
        })
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Certificate_Course_${courseId}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        toast.success('Certificate downloaded successfully!')
      } catch (error) {
        console.error('Certificate download error:', error)
        if (error.response?.status === 403) {
          toast.error('You must complete the course to download a certificate')
        } else {
          toast.error('Failed to download certificate')
        }
      }
    }

    const formatRelativeTime = (dateString) => {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffMins < 60) {
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
      } else {
        return date.toLocaleDateString()
      }
    }

    const getThumbnailUrl = (thumbnail) => {
      if (thumbnail.startsWith('http')) return thumbnail
      return `${process.env.VUE_APP_API_URL || ''}/media/${thumbnail}`
    }

    const getEmptyStateTitle = () => {
      if (currentFilter.value === 'in_progress') {
        return 'No courses in progress'
      } else if (currentFilter.value === 'completed') {
        return 'No completed courses yet'
      }
      return 'No enrolled courses yet'
    }

    const getEmptyStateMessage = () => {
      if (currentFilter.value === 'in_progress') {
        return 'Start learning from your enrolled courses to see them here.'
      } else if (currentFilter.value === 'completed') {
        return 'Complete your first course to see it here.'
      }
      return 'Explore our course catalog and enroll in courses to start learning.'
    }

    // Track last refresh time to avoid excessive API calls
    const lastRefreshTime = ref(0)
    const REFRESH_COOLDOWN_MS = 5000  // 5 seconds minimum between refreshes

    // Handle visibility change to refresh when user returns to tab/page
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now()
        if (now - lastRefreshTime.value > REFRESH_COOLDOWN_MS) {
          console.log('📊 MyLearning visible - refreshing progress data')
          lastRefreshTime.value = now
          await loadEnrolledCourses()
        }
      }
    }

    // Handle window focus (user clicks back into browser window)
    const handleWindowFocus = async () => {
      const now = Date.now()
      if (now - lastRefreshTime.value > REFRESH_COOLDOWN_MS) {
        console.log('📊 MyLearning focused - refreshing progress data')
        lastRefreshTime.value = now
        await loadEnrolledCourses()
      }
    }

    onMounted(() => {
      lastRefreshTime.value = Date.now()
      loadEnrolledCourses()
      
      // Listen for page visibility changes (user returns from video player)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      // Also listen for window focus
      window.addEventListener('focus', handleWindowFocus)
    })

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
    })

    return {
      enrolledCourses,
      loading,
      currentFilter,
      expandedEnrollments,
      stats,
      inProgressCourses,
      completedCourses,
      filteredCourses,
      continueCourse,
      downloadCertificate,
      formatCourseType,
      formatDuration,
      formatVideoDuration,
      formatRelativeTime,
      getThumbnailUrl,
      getEmptyStateTitle,
      getEmptyStateMessage,
      toggleVideoProgress,
      primaryColor,
      secondaryColor,
      accentColor,
      // Research Library
      getCourseResources,
      isResearchExpanded,
      toggleResearchExpanded,
      truncateText
    }
  }
}
</script>

<style scoped>
.my-learning-page {
  padding-bottom: 2.5rem;
}

.header-actions {
  display: flex;
  gap: var(--spacing-md);
  flex-shrink: 0;
}

/* Loading State */
.loading-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xxl);
}

.stat-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1.8rem;
  color: white;
}

.stat-icon.enrolled {
  background: linear-gradient(135deg, v-bind(primaryColor) 0%, v-bind(secondaryColor) 100%);
}

.stat-icon.completed {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
}

.stat-icon.in-progress {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.stat-icon.hours {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.stat-info h3 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
}

.stat-info p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-text-secondary);
}

/* Filter Section */
.filter-section {
  margin-bottom: var(--spacing-xl);
}

.filter-tabs {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.filter-tab {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: white;
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius);
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-tab:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.filter-tab.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

/* Courses Grid */
.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-xl);
}

.course-card {
  background: white;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.course-thumbnail {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, v-bind(primaryColor) 0%, v-bind(secondaryColor) 100%);
}

.course-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.5);
}

.progress-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  padding: var(--spacing-sm);
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: var(--spacing-xs);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4caf50 0%, #45a049 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
}

.course-content {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  flex: 1;
}

.course-meta {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
}

.course-type {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.completed-badge {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: #e8f5e9;
  color: #4caf50;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.course-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
  line-height: 1.4;
}

.course-description {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.6;
  font-size: 0.95rem;
  flex: 1;
}

.course-stats {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-lg);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border);
}

.stat {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.stat i {
  color: var(--color-primary);
  width: 16px;
}

/* Video Progress Section */
.video-progress-section {
  margin: var(--spacing-md) 0;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
}

.progress-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  background: #f8fafc;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
}

.progress-toggle:hover {
  background: #f1f5f9;
}

.toggle-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.toggle-header i {
  color: var(--color-primary);
}

.toggle-title {
  font-weight: 600;
  color: var(--color-text-primary);
}

.toggle-count {
  background: var(--color-primary);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.video-progress-list {
  padding: var(--spacing-sm);
  background: white;
  max-height: 300px;
  overflow-y: auto;
}

.video-progress-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  border-radius: 6px;
  margin-bottom: var(--spacing-xs);
  transition: background 0.2s ease;
}

.video-progress-item:hover {
  background: #f8fafc;
}

.video-progress-item.completed {
  background: rgba(16, 185, 129, 0.05);
}

.video-progress-item.in-progress {
  background: rgba(59, 130, 246, 0.05);
}

.video-status-icon {
  width: 24px;
  flex-shrink: 0;
}

.video-status-icon i {
  font-size: 1rem;
}

.video-progress-item.completed .video-status-icon i {
  color: #10b981;
}

.video-progress-item.in-progress .video-status-icon i {
  color: #3b82f6;
}

.video-progress-item:not(.completed):not(.in-progress) .video-status-icon i {
  color: #cbd5e1;
}

.video-progress-info {
  flex: 1;
  min-width: 0;
}

.video-title {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.video-progress-bar {
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  margin: 4px 0;
  overflow: hidden;
}

.video-progress-bar .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #10b981);
  border-radius: 2px;
}

.video-status-text {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}

.video-duration {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

/* Certification Requirements */
.certification-requirements {
  margin: var(--spacing-md) 0;
  padding: var(--spacing-md);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 8px;
}

.cert-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
  color: #d97706;
}

.cert-header i {
  font-size: 1rem;
}

.requirement {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) 0;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.requirement i {
  color: #cbd5e1;
  font-size: 1.1rem;
}

.requirement.complete i {
  color: #10b981;
}

.score-badge {
  margin-left: var(--spacing-xs);
  padding: 2px 8px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 12px;
  font-size: 0.8rem;
  color: #3b82f6;
  font-weight: 600;
}

/* Saved Research Section */
.saved-research-section {
  margin: var(--spacing-md) 0;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
}

.research-header-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  background: #f0fdf4;
  cursor: pointer;
  transition: background 0.2s ease;
}

.research-header-toggle:hover {
  background: #ecfdf5;
}

.research-header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-weight: 600;
  color: #166534;
}

.research-header-left i {
  color: #10b981;
}

.research-list {
  padding: var(--spacing-sm) var(--spacing-md);
  background: white;
}

.research-item {
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--color-border);
}

.research-item:last-child {
  border-bottom: none;
}

.research-link {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text-primary);
  text-decoration: none;
  line-height: 1.4;
  margin-bottom: 2px;
}

.research-link:hover {
  color: #10b981;
}

.research-journal {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}

.view-all-research {
  display: block;
  text-align: center;
  padding: var(--spacing-sm) 0;
  font-size: 0.85rem;
  color: #10b981;
  text-decoration: none;
  font-weight: 500;
}

.view-all-research:hover {
  text-decoration: underline;
}

.course-actions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-block {
  width: 100%;
}

/* Quiz Locked Notice */
.quiz-locked-notice {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(156, 163, 175, 0.1);
  border: 1px solid rgba(156, 163, 175, 0.3);
  border-radius: var(--border-radius);
  color: #6b7280;
  font-size: 0.85rem;
  width: 100%;
}

.quiz-locked-notice i {
  color: #9ca3af;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-certificate {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.btn-certificate:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.btn-primary:hover {
  background: var(--color-secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.empty-state i {
  font-size: 4rem;
  color: var(--color-primary);
  opacity: 0.5;
  margin-bottom: var(--spacing-lg);
}

.empty-state h2 {
  font-size: 1.5rem;
  margin: 0 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
}

.empty-state p {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-xl) 0;
}

/* Responsive */
@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .courses-grid {
    grid-template-columns: 1fr;
  }

  .filter-tabs {
    flex-direction: column;
  }

  .filter-tab {
    width: 100%;
  }
}
</style>

